import {neverland as $, render, html, useState, useEffect} from 'neverland';

import Collection_Table from './Collection_Table.js';
import Collection_Edit from './Collection_Doc.js';
import Collection_Archive from './Collection_Archive.js';
import LoadingProgressBar from './LoadingProgressBar.js';

import Select from './Select.js';
import TextInput from './TextInput.js';

function randomString() {
	return Math.random().toString().substr(2, 10);
}

function defaultFilters(specs) {
	let filters = {};
	for (let f of Object.keys(specs.fields)) {
		let field = specs.fields[f];
		if (field.type == 'select') {
			filters[f] = [];
		} else if (field.type == 'text' & field.filterable) {
			filters[f] = '';
		}
	}
	return filters;
}

const Collection = $(function(auth, specs) {
	
	let docsPerPage = 100;

	const [collection, setCollection] = useState(
		{
			...specs,
			mongo: auth.db.collection(specs.collectionName),

			search: "",
			allDocs: [],
			filteredDocs: [],
			
			showFilters: false,
			filters: defaultFilters(specs),
			filterQueryId: randomString(),
			pageNum: 1,
			pageCount: 'many',
			pageDelimeters: [],

			docID: null,
			docPaneID: null,
			
			edit: false,
			copy: false,
			preview: false,
			check: false,
			wizard: false,

			showArchived: false,
			archiveID: null,

			loading: true,
			refDataLoading: specs.refCollectionSpecs ? true : false,
			docsLoading: false,
			loadingProgress: 0
		}
	);

	let toggleFilters = () => {
		setCollection(prevCollection => {
			let collection = {...prevCollection};
			collection.showFilters = !collection.showFilters;
			return collection;
		})
	}

	function setPage(incr) {
		return () => {
			setCollection(prevCollection => {
				let collection = {...prevCollection};
				collection.pageNum = Math.max(1, Math.min(collection.pageNum + incr, collection.pageCount));
				return collection;
			})
		}
	}

	function refilterDocs() {

		setCollection(prevCollection => {

			let collection = {...prevCollection},
				searchString = document.querySelector('#search').value.toLowerCase(),
				viewSpecs = collection.views[collection.views.default];
			
			// Apply search terms.
			collection.filteredDocs = collection.allDocs.filter(function(d) {
			
				let isMatch = false;
			
				for (let col of viewSpecs) {
					if (d.hasOwnProperty(col.key)) {
						if (typeof(d[col.key]) == "string") {
							isMatch = isMatch || d[col.key].toLowerCase().includes(searchString);
						}
					}
				}

				return isMatch;
			
			});

			// Apply archive.
			if (!collection.showArchived) {
				collection.filteredDocs = collection.filteredDocs.filter(function(d) {
					return !Boolean(d.archived);
				})
			}

			return collection;

		})
	}

	useEffect(() => {
		// Fetch total number of documents.
		collection.mongo.count()
			.then(data => {
				setCollection(prevCollection => {
					let collection = {...prevCollection};
					collection.pageCount = Math.ceil(data / docsPerPage);
					return collection;
				})
			})
			.catch(err => {
				console.error('Failed to fetch document count:', err);
			})

		if (collection.refCollectionSpecs) {
			console.log('FETCHING REFERENCE COLLECTIONS...');
			let promises = [];
			let completedRefs = 0;
			const totalRefs = collection.refCollectionSpecs.length;
			let allRefData = {};

			for (let c of collection.refCollectionSpecs) {
				let mongo = auth.db.collection(c.name);
				promises.push(mongo.find()
					.then(data => {
						console.log(`   FETCHED: ${c.name}`);
						let refData = data.map(d => {
							d.id = d._id["$oid"].toString();
							d.value = d._id["$oid"].toString();
							let lab = '';
							c.labelFormat.forEach(function(lf) {
								let isUsed = lf.hasOwnProperty('if') ? (d.hasOwnProperty(lf.if) ? d[lf.if] !== '' : false) : true;
								if (isUsed) {
									if (lf.type == 'field') {
										lab += d[lf.value];	
									} else if (lf.type == 'text') {
										lab += lf.value;
									}
								}
							});
							d.label = lab;
							d.collection = c.name;
							d.disabled = Boolean(d.archived);
							return d;
						});
						
						allRefData[c.name] = refData;
						completedRefs++;
						return data;
					})
					.catch(async (err) => {
						console.log(`   CHUNKED FETCH: ${c.name}`);
						let allData = [];
						let skip = 0;
						const limit = 100;
						
						while (true) {
							try {
								const chunk = await mongo.find({}, { limit, skip });
								if (chunk.length === 0) break;
								allData = allData.concat(chunk);
								skip += limit;
							} catch (chunkErr) {
								break;
							}
						}
						
						let refData = allData.map(d => {
							d.id = d._id["$oid"].toString();
							d.value = d._id["$oid"].toString();
							let lab = '';
							c.labelFormat.forEach(function(lf) {
								let isUsed = lf.hasOwnProperty('if') ? (d.hasOwnProperty(lf.if) ? d[lf.if] !== '' : false) : true;
								if (isUsed) {
									if (lf.type == 'field') {
										lab += d[lf.value];	
									} else if (lf.type == 'text') {
										lab += lf.value;
									}
								}
							});
							d.label = lab;
							d.collection = c.name;
							d.disabled = Boolean(d.archived);
							return d;
						});
						
						allRefData[c.name] = refData;
						completedRefs++;
						return allData;
					})
				);
			}

			Promise.all(promises).then(async () => {
				let surveyItemSetIds = new Set();
				if (specs.collectionName === 'Surveys') {
					try {
						const surveyDocs = await auth.db.collection('Surveys').find({}, { projection: { itemSet: 1 } });
						surveyDocs.forEach(s => { if (s.itemSet) surveyItemSetIds.add(s.itemSet); });
					} catch(e) { console.warn('Could not fetch surveys for ItemSet filter', e); }
				}

				setCollection(prevCollection => {
					let collection = {...prevCollection};
					let { fields } = collection;
					
					// Update all reference fields with loaded data
					Object.keys(fields).forEach(function(f) {
						if (fields[f].isReference) {
							if (!fields[f].optionCollections) {
								fields[f].optionCollections = [...fields[f].options];
							}
							fields[f].options = [];
							for (let refCollectionName of fields[f].optionCollections) {
								if (allRefData[refCollectionName]) {
									fields[f].options = fields[f].options.concat(allRefData[refCollectionName]);
								}
							}
						} else if (fields[f].type == 'sequence') {
							let subfields = fields[f].fields;
							Object.keys(subfields).forEach(function(sf) {
								if (subfields[sf].isReference) {
									if (!subfields[sf].optionCollections) {
										subfields[sf].optionCollections = [...subfields[sf].options];
									}
									subfields[sf].options = [];
									for (let refCollectionName of subfields[sf].optionCollections) {
										if (allRefData[refCollectionName]) {
											subfields[sf].options = subfields[sf].options.concat(allRefData[refCollectionName]);
										}
									}
								} else if (subfields[sf].type == 'sequence') {
									let subsubfields = subfields[sf].fields;
									Object.keys(subsubfields).forEach(function(ssf) {
										if (subsubfields[ssf].isReference) {
											if (!subsubfields[ssf].optionCollections) {
												subsubfields[ssf].optionCollections = [...subsubfields[ssf].options];
											}
											subsubfields[ssf].options = [];
											for (let refCollectionName of subsubfields[ssf].optionCollections) {
												if (allRefData[refCollectionName]) {
													subsubfields[ssf].options = subsubfields[ssf].options.concat(allRefData[refCollectionName]);
												}
											}
										}
									})
								}
							})
						}
					})

					// Filter itemSet options to only those with surveyItemset: true.
					if (specs.collectionName === 'Surveys' && fields.itemSet && typeof fields.itemSet.options[0] === 'object') {
						fields.itemSet.options = fields.itemSet.options.filter(opt => opt.surveyItemset === true || (opt.hasResponses === true && surveyItemSetIds.has(opt.value)));
					}
					
					collection.refDataLoading = false;
					collection.loading = collection.docsLoading ? true : false;
					collection.loadingProgress = collection.docsLoading ? 75 : 100;
					collection.fullCollections = allRefData;
					collection.refCollections = allRefData;
					return collection;
				});
			});
		} else {
			setCollection(prevCollection => ({
				...prevCollection,
				refDataLoading: false,
				loading: prevCollection.docsLoading ? true : false,
				loadingProgress: prevCollection.docsLoading ? 75 : 100
			}));
		}
	}, [])


	useEffect(() => {
		
		let {
				pageNum,
				pageDelimeters
			} = collection,
			{ fields } = specs,
			filters = {};

		// Define MongoDB query filters.
		for (let f of Object.keys(collection.filters)) {
			let filter = collection.filters[f];
			
			switch(fields[f].type) {
				case 'select':
					if (filter.length > 0) {
						filters[f] = { $in: filter };
					}
					break;
				case 'text':
					if (filter.length > 0) {
						// filters['$text'] = { $search: filter };
						filters[f] = { $regex: filter, $options: "i"}
					}	
					break;
				default:
					break;
			}
		}

		// Fetch total number of documents.
		collection.mongo.count(filters)
			.then(data => {
				setCollection(prevCollection => {
					let collection = {...prevCollection};
					collection.pageCount = Math.max(1, Math.ceil(data / docsPerPage));
					return collection;
				})
			})
			.catch(err => {
				console.error('Failed to fetch filtered document count:', err);
			})

		// Add additional filter for relevant page of results.;
		if (pageNum > 1) {
			filters._id = {$lt: pageDelimeters[pageNum - 2]};
		}
		
		// console.log('New pageNum: ' + pageNum);

		console.log(`FETCHING DOCUMENTS (Page ${pageNum})...`);
		setCollection(prevCollection => ({...prevCollection, docsLoading: true, loadingProgress: prevCollection.refDataLoading ? 50 : 75}));
		collection.mongo.find(filters, {sort: {_id: -1}, limit: docsPerPage})
			.then(data => {
				console.log('FETCHED.')
				data = data.map(d => {
					d.id = d._id["$oid"].toString();
					return d;
				})
				setCollection(prevCollection => {
					let collection = {...prevCollection};
					collection.allDocs = data;
					collection.filteredDocs = data;
					collection.docsLoading = false;
					collection.loading = collection.refDataLoading ? true : false;
					collection.loadingProgress = collection.refDataLoading ? 75 : 100;
					if (pageNum > collection.pageDelimeters.length & data.length > 0) {
						collection.pageDelimeters.push(data[data.length - 1]._id);
					}
					return collection;
				})
				refilterDocs();
			})
			.catch(async (err) => {
				console.log(`CHUNKED FETCH DOCUMENTS (Page ${pageNum})...`);
				const limit = 1;
				let allData = [];
				
				for (let skip = 0; skip < docsPerPage; skip += limit) {
					try {
						const chunk = await collection.mongo.find(filters, {sort: {_id: -1}, limit, skip});
						if (chunk.length === 0) break;
						allData = allData.concat(chunk);
					} catch (chunkErr) {
						break;
					}
				}
				
				allData = allData.map(d => {
					d.id = d._id["$oid"].toString();
					return d;
				})
				
				setCollection(prevCollection => {
					let collection = {...prevCollection};
					collection.allDocs = allData;
					collection.filteredDocs = allData;
					collection.docsLoading = false;
					collection.loading = collection.refDataLoading ? true : false;
					collection.loadingProgress = collection.refDataLoading ? 75 : 100;
					if (pageNum > collection.pageDelimeters.length & allData.length > 0) {
						collection.pageDelimeters.push(allData[allData.length - 1]._id);
					}
					return collection;
				})
				refilterDocs();
			})

	}, [collection.pageNum, collection.filterQueryId])


	function refilterDocs() {

		setCollection(prevCollection => {

			let collection = {...prevCollection},
				searchString = document.querySelector('#search').value.toLowerCase(),
				viewSpecs = collection.views[collection.views.default];
			
			// Apply search terms.
			collection.filteredDocs = collection.allDocs.filter(function(d) {
			
				let isMatch = false;
			
				for (let col of viewSpecs) {
					if (d.hasOwnProperty(col.key)) {
						if (typeof(d[col.key]) == "string") {
							isMatch = isMatch || d[col.key].toLowerCase().includes(searchString);
						}
					}
				}

				return isMatch;
			
			});

			// Apply archive.
			if (!collection.showArchived) {
				collection.filteredDocs = collection.filteredDocs.filter(function(d) {
					return !Boolean(d.archived);
				})
			}

			return collection;

		})
	}

	useEffect(() => {
		refilterDocs()
	}, [
		collection.showArchived,
		collection.archiveID
	])

	function toggleArchived() {
		setCollection(prevCollection => {
			let collection = {...prevCollection};
			collection.showArchived = !collection.showArchived;
			return collection;
		})
	}

	function create() {
		setCollection(prevCollection => {
			let collection = {...prevCollection};
			collection.docID = 'new' + Math.random().toString(20).substr(2, 10);
			if (['Items','ItemSets'].includes(collection.collectionName)) {
				collection.preview = true;
			}
			collection.edit = true;
			collection.docPaneID = Math.random().toString(20).substr(2, 10);
			return collection;
		})
	}

	// <div class="counts">${collection.filteredDocs.length}<span>/</span>${collection.allDocs.length}</div>

	function FilterField(f) {

		let field = collection.fields[f];

		switch(field.type) {
			case 'text':
				return html`<div class="filter">
					<div class="text">
						${TextInput(
							collection.filters[f],
							val => setCollection(prevCollection => {
								let collection = {...prevCollection};
								collection.filters[f] = val;
								return collection;
							})
						)}
						<button onclick=${() => {
							setCollection(prevCollection => {
								let collection = {...prevCollection};
								collection.pageNum = 1;
								collection.pageDelimeters = [];
								collection.filterQueryId = randomString();
								return collection;
							})
						}}>🔍</button>
					</div>
					<label>${field.title}</label>
				</div>`;
				break;
			case 'select':
				return html`<div class="filter">
					${Select(
						field.options,
						collection.filters[f],
						val => {
							setCollection(prevCollection => {
								let collection = {...prevCollection};
								collection.filters[f] = val;
								collection.pageNum = 1;
								collection.pageDelimeters = [];
								collection.filterQueryId = randomString();
								return collection;
							})
						},
						false,
						[],
						true,
						true)}
					<label>${field.title}</label>
				</div>`;
				break;
			default:
				return html``;
				break;
		}
	}

	return html`
		<div class="main">

			<div class="controls">
				<button class="filter ${collection.showFilters ? 'active' : ''}" onclick="${toggleFilters}">≡</button>
				<input
					type="text"
					id="search"
					placeholder="Type to search..."
					autofocus
					oninput="${refilterDocs}">
				</input>
				
				<button class="pagination" ?disabled="${collection.pageCount === 'many'}" onclick="${collection.pageCount === 'many' ? null : setPage(-1)}">◄</button>
				<div class="pagination">
					 Page ${collection.pageNum}
					of ${collection.pageCount}</div>
				<button class="pagination" ?disabled="${collection.pageCount === 'many'}" onclick="${collection.pageCount === 'many' ? null : setPage(1)}">►</button>
				
				${auth.isAdmin && collection.collectionName !== 'CSVDownloads' ? html`<button class="green" onclick="${create}">
					Create new
				</button>` : ''}
			</div>

			<div class="filters ${collection.showFilters ? '' : 'hide'}">

				${Object.keys(collection.fields).filter(f => (collection.fields[f].type == 'select' && typeof(collection.fields[f].options[0]) == 'object') | collection.fields[f].type == 'text' & collection.fields[f].filterable).map(f => FilterField(f))}

				<div class="filter">
					<button onclick="${toggleArchived}">
						${collection.showArchived ? 'Hide' : 'Show'} archived
					</button>
				</div>
			</div>

			${(collection.loading || collection.refDataLoading) ? LoadingProgressBar(collection.loadingProgress, collection.refDataLoading ? 'Loading reference data...' : 'Loading documents...') : Collection_Table(auth, collection, setCollection)}

		</div>	

		${(collection.docID === null)
			? html``
			: Collection_Edit(auth, collection, setCollection)}

		${(collection.archiveID === null)
			? html``
			: Collection_Archive(auth, collection, setCollection)}

	`;

});

export default Collection;